import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import ModalFormFeedback from '../ModalFormFeedback/ModalFormFeedback';
import {SelectValuesFormSimple} from '../../services/content/Constants';
import {content} from '../../services/content';
import {IContactForms} from '../../redux/initState/InterfacesState';

interface Props {
  label?: string;
  contactForms: IContactForms;
  modalTitle?: string;
}

/**
 * Форма в hero-блоке (блок mk_hero).
 *
 * В сниппете кнопка только диспатчила CustomEvent 'mk-hero-submit' — заявка
 * никуда не уходила. Здесь тот же сценарий, что на страницах услуг
 * (content-header → MainBlockForm): телефон подставляется в модалку обратной
 * связи, а отправка идёт общим путём SmartCaptcha → CF7 → amoCRM.
 *
 * Разметка и классы .mk-hero-* оставлены как в сниппете, стили переехали в
 * css/mk-blocks.css без правок. Маска телефона — тоже перенесённая логика
 * сниппета: показываем +7 (999) 999-99-99 — ровно в этом виде маска модалки
 * (maskito, '+7 (___) ___-__-__') и принимает значение, поэтому телефон уходит
 * туда как есть, без нормализации.
 */

const digitsOnly = (value: string) => value.replace(/\D/g, '');

const normalize = (value: string) => {
  let d = digitsOnly(value);
  if (d.charAt(0) === '7' || d.charAt(0) === '8') {
    d = d.slice(1);
  }
  return d.slice(0, 10);
};

const format = (d: string) => {
  if (!d.length) return '';
  let out = '+7 (' + d.slice(0, 3);
  if (d.length > 3) out += ') ' + d.slice(3, 6);
  if (d.length > 6) out += '-' + d.slice(6, 8);
  if (d.length > 8) out += '-' + d.slice(8, 10);
  return out;
};

const MkHeroForm = ({label, contactForms, modalTitle}: Props) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  const digits = normalize(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(format(normalize(e.target.value)));
    setError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (digits.length !== 10) {
      setError('Введите номер полностью');
      return;
    }
    setError('');
    setShowFeedback(true);
  };

  return (
    <form className="mk-hero-form" onSubmit={handleSubmit} noValidate>
      <label className="mk-hero-label" htmlFor="mk-hero-phone">{label || 'Рассчитать тариф'}</label>
      <div className="mk-hero-row">
        <input
          className={error ? 'mk-hero-input is-error' : 'mk-hero-input'}
          type="tel"
          id="mk-hero-phone"
          name="phone"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+7 (___) ___-__-__"
          aria-describedby="mk-hero-phone-error"
          value={value}
          onChange={handleChange}
        />
        <button type="submit" className="mk-hero-submit" aria-label="Отправить заявку">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4"
                  strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <span className="mk-hero-error" id="mk-hero-phone-error" role="alert" aria-live="polite">{error}</span>

      {createPortal(
        <ModalFormFeedback
          hasCssTransition
          showForm={showFeedback}
          id="feedback"
          title={modalTitle || content.header.textModalFeedback}
          select={{options: SelectValuesFormSimple, active: SelectValuesFormSimple[1]}}
          closedModal={() => setShowFeedback(false)}
          phoneNumber={value || '+7'}
          contactForms={contactForms}
        />,
        document.body
      )}
    </form>
  );
};

export default MkHeroForm;
